/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {
  BillingDetailsComponent, ComponentList, OperationResponse, TrueTime, CustomerAccount,
  BillingProfile, InvoiceType,
} from 'in-time-core';

import { Injectable, inject } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { AccountService } from './account.service';
import { SignUpCredentials } from './models/sign-up-credentials';
import { ErrorType } from '../core/models/error-type';
import { createAddressFromBillingDetails } from '../core/models/billing-address-details';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class SignUpService {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthenticationService);
  private readonly accountService = inject(AccountService);

  async signUpWithEmailAndPassword(credentials: SignUpCredentials, options: { allowPlatformMarketing: boolean | null }): Promise<OperationResponse<CustomerAccount>> {
    const accountResponse = this.generateCustomerAccount(credentials);
    if(!accountResponse.success) {
      return accountResponse;
    }

    if(this.authService.authState.isAuthenticated) {
      await this.authService.signOut();
    }

    if(credentials.email == null || credentials.password == null) {
      return OperationResponse.error(ErrorType.CredentialsNotValid);
    }

    const authState = await this.authService.signUpWithEmailAndPassword(credentials.email, credentials.password);
    if(!authState.isAuthenticated) {
      return OperationResponse.error(authState.error!);
    }

    return this.createAccount(
      accountResponse.data.copyWith({ uniqueId: authState.user!.uid }),
      options,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async signUpWithSocial(credentials: SignUpCredentials): Promise<OperationResponse<CustomerAccount>> {
    return OperationResponse.error(ErrorType.NotSupported);
  }

  private async createAccount(account: CustomerAccount, options: { allowPlatformMarketing: boolean | null }): Promise<OperationResponse<CustomerAccount>> {
    const response = await this.apiService.createCustomerAccount(account, options);
    if(response.success) {
      this.accountService.onNewAccountCreated(account);
    }

    return response;
  }

  private generateCustomerAccount(credentials: SignUpCredentials): OperationResponse<CustomerAccount> {
    const account = new CustomerAccount(
      '',
      credentials.firstName,
      credentials.lastName,
      credentials.email,
      credentials.phoneNumber,
      null,
      null,
      TrueTime.now(),
      new ComponentList(),
    );

    if(credentials.billingDetails != null) {
      const billingDetails = credentials.billingDetails;
      const address = createAddressFromBillingDetails(billingDetails.address);

      if(billingDetails.invoiceType == InvoiceType.PhysicalPerson) {
        account.addOrReplaceComponent(BillingDetailsComponent.fromProfile(BillingProfile.personal({
          name: billingDetails.billingName,
          email: billingDetails.email,
          phoneNumber: billingDetails.phoneNumber,
          address: address,
        })));
      }
      else if(billingDetails.invoiceType == InvoiceType.LegalEntity) {
        if(billingDetails.phoneNumber == null || billingDetails.taxCode == null || address == null) {
          return OperationResponse.error(ErrorType.BillingDetailsNotValid);
        }

        account.addOrReplaceComponent(BillingDetailsComponent.fromProfile(BillingProfile.legalEntity({
          name: billingDetails.billingName,
          email: billingDetails.email,
          phoneNumber: billingDetails.phoneNumber,
          address: address,
          taxCode: billingDetails.taxCode,
          groupTaxCode: billingDetails.groupTaxCode,
        })));
      }
    }

    return OperationResponse.success(account);
  }
}
